<script setup lang="ts">
import { BREAKPOINTS, IS_IBG_MANAGER } from "@/consts";
import pixelToRem from "@/helpers/shared/pixelToRem";
import { useAuthStore } from "@/stores/authStore";
import DropdownLinkBuilder from "@/stories/buttons/DropdownButton/DropdownLinkBuilder.vue";
import SVG from "@/stories/displays/SVG/SVG.vue";
import Avatar from "@/stories/media/Avatar/Avatar.vue";
import { onClickOutside, useBreakpoints } from "@vueuse/core";
import { ref, type CSSProperties } from "vue";
import { useRouter } from "vue-router";

const emit = defineEmits<{ logout: [] }>();

const router = useRouter();
const authStore = useAuthStore();
const breakpoints = useBreakpoints(BREAKPOINTS);
const isDesktop = breakpoints.greaterOrEqual("desktop");

const triggerRef = ref<HTMLDivElement>();
const isActive = ref(false);
const contentStyle = ref<CSSProperties>({});

const handleOver = () => {
  if (!triggerRef.value) {
    return;
  }

  if (isActive.value) {
    isActive.value = false;
    return;
  }

  isActive.value = true;
  const rect = triggerRef.value.getBoundingClientRect();

  contentStyle.value.left = pixelToRem(rect.left + rect.width + 14);
  contentStyle.value.bottom = pixelToRem(31);
};

onClickOutside(triggerRef, () => {
  isActive.value = false;
});

const handleRouteClick = (path: string) => {
  router.push(path);
  isActive.value = false;
};

const triggerEvent = (name: unknown) => {
  emit(name as never);
  isActive.value = false;
};
</script>

<template>
  <div ref="triggerRef" class="w-full flex justify-center">
    <button
      type="button"
      :class="[
        'w-[3.5rem] h-[2.5rem] flex justify-center h100 items-center rounded-lg transition-colors cursor-pointer text-white',
        'desktop:w-full desktop:h-auto desktop:gap-3 desktop:justify-start desktop:p-3',
        'hover:bg-darkOcean600',
      ]"
      @click="handleOver"
    >
      <div v-if="isDesktop" class="w-full flex items-center gap-2">
        <Avatar
          :src="authStore.profile?.image?.source"
          :alt="
            authStore.profile?.displayName || authStore.profile?.email || ''
          "
          :size="1.875"
        />

        <div class="text-left grow">
          <p class="b100">{{ $t("global.loggedInAs") }}</p>
          <p class="h50 w-full truncate">
            {{ authStore.profile?.email }}
          </p>
        </div>

        <div>
          <SVG icon="far-chevron-right" height="1rem" width="1rem"></SVG>
        </div>
      </div>
      <SVG
        v-else
        icon="far-elipsis-vertical"
        width="1.5rem"
        height="1.5rem"
      ></SVG>
    </button>

    <div
      v-if="isActive"
      class="fixed bg-white border border-neutral300 p-2 rounded-lg shadow-lg min-w-[14.375rem] text-neutral1000"
      :style="contentStyle"
    >
      <div
        class="px-1 py-3 border-b border-neutral300 flex justify-between items-center gap-2"
      >
        <Avatar
          :src="authStore.profile?.image?.source"
          :alt="
            authStore.profile?.displayName || authStore.profile?.email || ''
          "
          :size="1.875"
        />
        <div class="flex-grow text-neutral1000">
          <p class="b200">{{ $t("global.loggedInAs") }}</p>
          <p class="h200 truncate">{{ authStore.profile?.email }}</p>
        </div>
      </div>
      <DropdownLinkBuilder
        v-if="IS_IBG_MANAGER"
        :item="{
          title: $t('support.navigation_link'),
          svg: 'far-circle-question',
          iconRight:
            'far fa-chevron-right text-sm font-extrabold text-neutral700',
          value: 'support',
        }"
        @click="handleRouteClick('/support')"
      />
      <DropdownLinkBuilder
        v-if="authStore.isSupport && IS_IBG_MANAGER"
        :item="{
          title: $t('global.admin'),
          svg: 'far-user-gear',
          iconRight:
            'far fa-chevron-right text-sm font-extrabold text-neutral700',
          value: 'admin',
        }"
        @click="handleRouteClick('/admin')"
      />
      <DropdownLinkBuilder
        :item="{
          title: $t('main.logout'),
          svg: 'far-sign-out',
          iconRight:
            'far fa-chevron-right text-sm font-extrabold text-neutral700',
          value: 'logout',
        }"
        @click="triggerEvent('logout')"
      />
    </div>
  </div>
</template>
